import { authFetch, handleSessionExpired } from './authService';

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8001'}/api/v1`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    // authFetch a déjà tenté le refresh — session réellement expirée.
    handleSessionExpired(response);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
    const detail = error.detail;
    if (Array.isArray(detail)) {
      // Pydantic validation errors: [{loc, msg, type}, ...]
      const msg = detail.map((e: any) => e.msg).join(', ');
      throw new Error(msg || 'Erreur de validation');
    }
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      // Erreur métier structurée : { code, message, ... }
      // (cf. PROD1.5.5 prefix_overlap / storage_config_incomplete)
      // Si `errors` est une liste (JSON Schema validation côté /validate),
      // on les concatène au message pour que l'analyste voie les champs.
      let msg = detail.message;
      if (Array.isArray(detail.errors) && detail.errors.length > 0) {
        msg += ' : ' + detail.errors.join(' ; ');
      }
      throw new Error(msg);
    }
    throw new Error(typeof detail === 'string' ? detail : 'Erreur réseau');
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

export const apiClient = {
  async get<T>(url: string): Promise<{ data: T }> {
    const response = await authFetch(`${API_BASE}${url}`);
    const data = await handleResponse<T>(response);
    return { data };
  },

  async post<T>(url: string, body?: any): Promise<{ data: T }> {
    const response = await authFetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await handleResponse<T>(response);
    return { data };
  },

  async put<T>(url: string, body: any): Promise<{ data: T }> {
    const response = await authFetch(`${API_BASE}${url}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await handleResponse<T>(response);
    return { data };
  },

  async patch<T>(url: string, body: any): Promise<{ data: T }> {
    const response = await authFetch(`${API_BASE}${url}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await handleResponse<T>(response);
    return { data };
  },

  async delete(url: string): Promise<void> {
    const response = await authFetch(`${API_BASE}${url}`, {
      method: 'DELETE',
    });
    await handleResponse(response);
  },
};
